import intl from 'react-intl-universal'
import _ from 'lodash'

import denormalizer from '~/reactapp/story/util/denormalizer'

const isImageElement = (ce) => ce.type === 'image'

const isUnpublishedImage = (ce) => {
  return (
    isImageElement(ce) &&
    ce._id &&
    ce._id.length &&
    ce.additional_properties &&
    !ce.additional_properties.published
  )
}

export const getUnpublishedImages = async (storyObj) => {
  const nonReferenceImages = _.get(
    storyObj,
    'related_content.basic',
    []
  ).filter((item) => item.type === 'image')
  const imageReferences = _.get(storyObj, 'related_content.basic', []).filter(
    (item) => item.type === 'reference' && item.referent.type === 'image'
  )
  const denormalizedImages = imageReferences.length
    ? await denormalizer(imageReferences)
    : []

  const unpublishedImagesOnStory = _.get(storyObj, 'content_elements', [])
    .concat(
      Object.keys(_.get(storyObj, 'promo_items', {})).map(
        (promoItemId) => storyObj.promo_items[promoItemId]
      )
    )
    .concat([...nonReferenceImages, ...denormalizedImages])
    .filter(isUnpublishedImage)

  return unpublishedImagesOnStory.reduce((images, img) => {
    return { ...images, [img._id]: img }
  }, {})
}

export const hasGlobalOrWebsitePPPermission = (
  userPermission,
  canonicalWebsite
) => {
  const {
    permissions: { Anglerfish: afPermissions = {} } = {},
  } = userPermission
  const hasGlobalPP =
    Array.isArray(afPermissions.global) &&
    afPermissions.global.indexOf('PP') >= 0
  const hasWebsitePP =
    afPermissions.sites &&
    Array.isArray(afPermissions.sites[canonicalWebsite]) &&
    afPermissions.sites[canonicalWebsite].indexOf('PP') >= 0

  return !!(hasGlobalPP || hasWebsitePP)
}

const getAnglerfishPermission = () => {
  return fetch('/user', {
    method: 'get',
    credentials: 'same-origin',
  })
}

export const hasPublishPhotoPermission = (canonicalWebsite) => {
  return getAnglerfishPermission()
    .then((response) => {
      //  for local testing
      // return response.data
      if (!response.ok) {
        throw new Error(
          intl.get('story.imagePublish.errorPermission', {
            message: `${response.status} ${response.statusText}`,
          })
        )
      }
      return response.json()
    })
    .then((userPermission) => {
      return hasGlobalOrWebsitePPPermission(userPermission, canonicalWebsite)
    })
}

export const publishImages = (unpublishedImages) => {
  const publishImagePromises = Object.keys(unpublishedImages).map((id) => {
    const photo = unpublishedImages[id]
    photo.additional_properties.published = true
    const itemType = photo.type === 'image' ? 'photos' : 'galleries'
    const url = `/photo/api/v2/${itemType}/${id}`
    const headers = {
      'Content-Type': 'application/json',
      'Arc-App-Name': 'ellipsis',
      'Arc-Priority': 'standard',
    }
    // GET the latest data from anglerfish first as it doesn't support partial update currently
    return fetch(url, {
      headers,
      method: 'get',
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            intl.get('story.imagePublish.errorRetrieve', {
              message: `${response.status} ${response.statusText}`,
            })
          )
        }
        return response.json()
      })
      .then((photoData) => {
        photoData.additional_properties.published = true
        // PUT request to anglerfish api with updated publish flag set
        return fetch(url, {
          headers,
          method: 'PUT',
          credentials: 'same-origin',
          body: JSON.stringify(photoData),
        })
      })
  })

  return Promise.all(publishImagePromises).then((results) => {
    results.forEach((response) => {
      if (!response.ok) {
        throw new Error(
          intl.get('story.imagePublish.errorPublish', {
            message: `${response.status} ${response.statusText}`,
          })
        )
      }
    })

    const imgCnt = Object.keys(unpublishedImages).length
    return {
      message: intl.get('story.imagePublish.success', { imgCnt }),
    }
  })
}

export const publishUnpublishedImages = (storyObj) => {
  return getUnpublishedImages(storyObj).then((unpublishedImages) => {
    if (Object.keys(unpublishedImages).length === 0) {
      return new Promise((resolve) => {
        resolve({
          unpublishedImagesFound: false,
          message: intl.get('story.imagePublish.noUnpublishedImages'),
        })
      })
    }

    return hasPublishPhotoPermission().then((hasPPPermission) => {
      if (!hasPPPermission) {
        return {
          unpublishedImagesFound: true,
          hasPPPermission: false,
          message: intl.get('story.imagePublish.noPermission'),
        }
      }
      return publishImages(unpublishedImages).then((response) => {
        return {
          ...response,
          unpublishedImagesFound: true,
          hasPPPermission: true,
        }
      })
    })
  })
}
